/* eslint-disable react-refresh/only-export-components -- hook and component are co-located */
import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

interface RealtimeManagerContextValue {
  realtimeKey: number;
  isIdle: boolean;
}

const RealtimeManagerContext = createContext<RealtimeManagerContextValue>({
  realtimeKey: 0,
  isIdle: false,
});

const HIDDEN_TAB_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
const INACTIVE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const ACTIVITY_DEBOUNCE_MS = 1000;

export function RealtimeManagerProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [isIdle, setIsIdle] = useState(false);
  const [realtimeKey, setRealtimeKey] = useState(0);

  const hiddenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inactiveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isIdleRef = useRef(false); // Mirror state to avoid stale closures

  const goIdle = useCallback(() => {
    if (isIdleRef.current) return;
    isIdleRef.current = true;
    setIsIdle(true);
    supabase.removeAllChannels();
    console.log('[RealtimeManager] Idle — disconnected all channels');
  }, []);

  const wakeUp = useCallback(() => {
    if (!isIdleRef.current) return;
    isIdleRef.current = false;
    setIsIdle(false);
    setRealtimeKey((k) => k + 1);
    queryClient.invalidateQueries();
    console.log('[RealtimeManager] Active — reconnecting subscriptions');
  }, [queryClient]);

  // Reset the inactivity timer on user interaction
  const resetInactiveTimer = useCallback(() => {
    if (inactiveTimerRef.current) clearTimeout(inactiveTimerRef.current);
    inactiveTimerRef.current = setTimeout(goIdle, INACTIVE_TIMEOUT_MS);
  }, [goIdle]);

  // Debounced activity handler
  const onActivity = useCallback(() => {
    if (debounceRef.current) return;
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
    }, ACTIVITY_DEBOUNCE_MS);

    // If idle and tab is visible, wake up
    if (isIdleRef.current && document.visibilityState === 'visible') {
      wakeUp();
    }
    resetInactiveTimer();
  }, [wakeUp, resetInactiveTimer]);

  useEffect(() => {
    // Visibility change handler
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Start hidden-tab timer
        hiddenTimerRef.current = setTimeout(goIdle, HIDDEN_TAB_TIMEOUT_MS);
      } else {
        // Tab became visible again
        if (hiddenTimerRef.current) {
          clearTimeout(hiddenTimerRef.current);
          hiddenTimerRef.current = null;
        }
        if (isIdleRef.current) {
          wakeUp();
        }
        resetInactiveTimer();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('mousemove', onActivity);
    document.addEventListener('keydown', onActivity);
    document.addEventListener('touchstart', onActivity);
    document.addEventListener('scroll', onActivity, true);

    // Start initial inactivity timer
    resetInactiveTimer();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      document.removeEventListener('mousemove', onActivity);
      document.removeEventListener('keydown', onActivity);
      document.removeEventListener('touchstart', onActivity);
      document.removeEventListener('scroll', onActivity, true);
      if (hiddenTimerRef.current) clearTimeout(hiddenTimerRef.current);
      if (inactiveTimerRef.current) clearTimeout(inactiveTimerRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [goIdle, wakeUp, onActivity, resetInactiveTimer]);

  return (
    <RealtimeManagerContext.Provider value={{ realtimeKey, isIdle }}>
      {children}
    </RealtimeManagerContext.Provider>
  );
}

export function useRealtimeKey() {
  return useContext(RealtimeManagerContext);
}
